import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../App';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  FaShieldAlt, FaExclamationTriangle, FaCheck, FaBan, 
  FaEnvelope, FaPhone, FaUser, FaEye, FaTimes 
} from 'react-icons/fa';

export default function ContentViolations() {
  const { API } = useContext(AppContext);
  const [violations, setViolations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // 'pending', 'all', 'reviewed'
  const [selectedViolation, setSelectedViolation] = useState(null);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch stats
      const statsRes = await axios.get(`${API}/admin/content-violations/stats`);
      setStats(statsRes.data);
      
      // Fetch violations based on filter
      let url = `${API}/admin/content-violations`;
      if (filter === 'pending') {
        url += '?reviewed=false';
      } else if (filter === 'reviewed') {
        url += '?reviewed=true';
      }
      
      const violationsRes = await axios.get(url);
      setViolations(violationsRes.data);
    } catch (error) {
      console.error('Error fetching violations:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (violationId, action) => {
    try {
      await axios.put(`${API}/admin/content-violations/${violationId}/review?action=${action}`);
      toast.success(
        action === 'dismissed' ? 'Violation classée sans suite' :
        action === 'warned' ? 'Utilisateur averti' :
        'Utilisateur banni'
      );
      fetchData();
      setSelectedViolation(null);
    } catch (error) {
      console.error('Error reviewing violation:', error);
      toast.error('Erreur lors du traitement');
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      'quote': 'Devis',
      'rental': 'Location',
      'project_chat': 'Chat Projet',
      'order': 'Commande'
    };
    return labels[type] || type;
  };

  const getTypeColor = (type) => {
    const colors = {
      'quote': 'bg-blue-100 text-blue-800',
      'rental': 'bg-[#635BFF]/10 text-[#5248d9]',
      'project_chat': 'bg-purple-100 text-purple-800',
      'order': 'bg-[#00D4FF]/10 text-[#00B4D8]'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading && !stats) {
    return (
      <AdminLayout>
        <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
          <div className="text-xl text-gray-600">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#F5F5F5]">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-[#0A2540] mb-2 flex items-center gap-3">
              <FaShieldAlt className="text-[#635BFF]" />
              Modération des Messages
            </h1>
            <p className="text-gray-600">
              Surveillance des tentatives de contact hors plateforme
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Violations</p>
                    <p className="text-2xl font-bold text-[#0A2540]">{stats?.total || 0}</p>
                  </div>
                  <FaExclamationTriangle className="text-3xl text-yellow-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">En Attente</p>
                    <p className="text-2xl font-bold text-[#00D4FF]">{stats?.pending || 0}</p>
                  </div>
                  <FaEye className="text-3xl text-orange-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Traitées</p>
                    <p className="text-2xl font-bold text-[#635BFF]">{stats?.reviewed || 0}</p>
                  </div>
                  <FaCheck className="text-3xl text-[#635BFF]" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Récidivistes</p>
                    <p className="text-2xl font-bold text-red-500">{stats?.top_offenders?.length || 0}</p>
                  </div>
                  <FaBan className="text-3xl text-red-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Type Distribution */}
          {stats?.by_type && Object.keys(stats.by_type).length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-lg">Distribution par Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3">
                  {Object.entries(stats.by_type).map(([type, count]) => (
                    <Badge key={type} className={getTypeColor(type)}>
                      {getTypeLabel(type)}: {count}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={filter === 'pending' ? 'default' : 'outline'}
              onClick={() => setFilter('pending')}
              className={filter === 'pending' ? 'bg-[#0A2540]' : ''}
            >
              En Attente ({stats?.pending || 0})
            </Button>
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              onClick={() => setFilter('all')}
              className={filter === 'all' ? 'bg-[#0A2540]' : ''}
            >
              Toutes
            </Button>
            <Button
              variant={filter === 'reviewed' ? 'default' : 'outline'}
              onClick={() => setFilter('reviewed')}
              className={filter === 'reviewed' ? 'bg-[#0A2540]' : ''}
            >
              Traitées
            </Button>
          </div>

          {/* Violations List */}
          <Card>
            <CardHeader>
              <CardTitle>Violations Détectées</CardTitle>
            </CardHeader>
            <CardContent>
              {violations.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FaShieldAlt className="text-5xl mx-auto mb-4 text-[#635BFF]" />
                  <p>Aucune violation détectée</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {violations.map((violation) => (
                    <div 
                      key={violation.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-3">
                          <Badge className={getTypeColor(violation.message_type)}>
                            {getTypeLabel(violation.message_type)}
                          </Badge>
                          {violation.reviewed ? (
                            <Badge variant="outline" className="text-[#635BFF] border-[#635BFF]">
                              ✓ Traité: {violation.action_taken}
                            </Badge>
                          ) : (
                            <Badge variant="destructive">En attente</Badge>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(violation.created_at).toLocaleString('fr-FR')}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 mb-3 text-sm">
                        <FaUser className="text-gray-400" />
                        <span className="font-medium">{violation.user_name}</span>
                        <span className="text-gray-400">({violation.user_email})</span>
                      </div>

                      {/* Violations detected */}
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Infractions détectées:</p>
                        <div className="flex flex-wrap gap-2">
                          {violation.violations?.map((v, idx) => (
                            <Badge key={idx} variant="outline" className="text-red-600 border-red-300">
                              {v.includes('Email') && <FaEnvelope className="mr-1" />}
                              {v.includes('Téléphone') && <FaPhone className="mr-1" />}
                              {v}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Original message preview */}
                      <div className="bg-gray-100 rounded p-3 mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">Message original:</p>
                        <p className="text-sm text-gray-600 break-all">
                          {violation.original_message?.substring(0, 200)}
                          {violation.original_message?.length > 200 && '...'}
                        </p>
                      </div>

                      {/* Actions */}
                      {!violation.reviewed && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReview(violation.id, 'dismissed')}
                          >
                            <FaTimes className="mr-1" /> Classer sans suite
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-yellow-600 border-yellow-600 hover:bg-yellow-50"
                            onClick={() => handleReview(violation.id, 'warned')}
                          >
                            <FaExclamationTriangle className="mr-1" /> Avertir
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleReview(violation.id, 'banned')}
                          >
                            <FaBan className="mr-1" /> Bannir
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Offenders */}
          {stats?.top_offenders?.length > 0 && (
            <Card className="mt-8">
              <CardHeader>
                <CardTitle className="text-red-600 flex items-center gap-2">
                  <FaBan /> Utilisateurs Récidivistes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Utilisateur</th>
                        <th className="text-left py-2">Email</th>
                        <th className="text-center py-2">Violations</th>
                        <th className="text-right py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.top_offenders.map((offender) => (
                        <tr key={offender.user_id} className="border-b hover:bg-gray-50">
                          <td className="py-3">{offender.user_name}</td>
                          <td className="py-3 text-gray-600">{offender.user_email}</td>
                          <td className="py-3 text-center">
                            <Badge variant="destructive">{offender.violation_count}</Badge>
                          </td>
                          <td className="py-3 text-right">
                            <Button size="sm" variant="outline">
                              Voir détails
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
